import React, { useState } from 'react';
import { FileText, Upload, Trash2, Download, X } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useQueryClient } from '@tanstack/react-query';
import ErrorMessage from '../ErrorMessage';

interface Document {
  id: string;
  organization_id: string;
  entity_id: string;
  file_name: string;
  file_size: number;
  content_type: string;
  uploaded_by: string;
  uploaded_at: string;
  updated_at: string;
}

const EntityDocuments: React.FC = () => {
  const { clientId, organizationId } = useParams<{ clientId: string; organizationId: string }>();
  const { currentOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const orgId = organizationId || currentOrganization?.id || '';
  const entityId = clientId || '';

  // Fetch documents from API
  const documentsQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities/{entity_id}/documents',
    {
      params: {
        path: {
          organization_id: orgId,
          entity_id: entityId,
        },
      },
    },
    {
      enabled: !!orgId && !!entityId,
    }
  );

  const [isUploading, setIsUploading] = useState(false);

  // Delete document mutation
  const deleteDocumentMutation = $api.useMutation(
    'delete',
    '/api/organization/{organization_id}/entities/{entity_id}/documents/{document_id}'
  );

  const documents = documentsQuery.data?.documents || [];
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileArray = Array.from(e.target.files);
      setSelectedFiles(fileArray);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    const uploadPromises = selectedFiles.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const baseUrl = import.meta.env.VITEST 
        ? 'https://sanctions-api.sacha-29f.workers.dev'
        : '';
      const url = `${baseUrl}/api/organization/${orgId}/entities/${entityId}/documents`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erreur lors du téléversement');
      }

      return response.json();
    });

    try {
      await Promise.all(uploadPromises);
      // Invalidate and refetch documents
      queryClient.invalidateQueries({
        queryKey: ['get', '/api/organization/{organization_id}/entities/{entity_id}/documents'],
      });
      documentsQuery.refetch();
      setSelectedFiles([]);
      // Reset file input
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error uploading files:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors du téléversement des fichiers');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    deleteDocumentMutation.mutate(
      {
        params: {
          path: {
            organization_id: orgId,
            entity_id: entityId,
            document_id: documentId,
          },
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['get', '/api/organization/{organization_id}/entities/{entity_id}/documents'],
          });
          documentsQuery.refetch();
        },
      }
    );
  };

  const handleDownload = (documentId: string, fileName: string) => {
    // Download document
    const token = localStorage.getItem('auth_token');
    const baseUrl = import.meta.env.VITEST 
      ? 'https://sanctions-api.sacha-29f.workers.dev'
      : '';
    const url = `${baseUrl}/api/organization/${orgId}/entities/${entityId}/documents/${documentId}/download`;
    
    fetch(url, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Erreur lors du téléchargement');
        }
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(error => {
        console.error('Error downloading document:', error);
        alert('Erreur lors du téléchargement du document');
      });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (documentsQuery.isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement des documents...</span>
        </div>
      </div>
    );
  }

  if (documentsQuery.error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <ErrorMessage error={documentsQuery.error} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
        <FileText className="h-5 w-5" />
        <span>Documents</span>
      </h3>

      {/* Upload Section */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ajouter des documents
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
          <div className="text-center">
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <input
              id="file-upload"
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Cliquer pour sélectionner des fichiers
            </label>
            <p className="text-xs text-gray-500 mt-1">PDF, JPG ou PNG</p>
          </div>

          {/* Selected files preview */}
          {selectedFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-900">{file.name}</span>
                    <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                  </div>
                  <button
                    onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="w-full mt-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Upload en cours...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    <span>Téléverser {selectedFiles.length} fichier{selectedFiles.length > 1 ? 's' : ''}</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error Messages */}
      {deleteDocumentMutation.error && (
        <ErrorMessage error={deleteDocumentMutation.error} className="mb-4" />
      )}

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Aucun document enregistré</p>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((doc: Document) => (
            <div key={doc.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{doc.file_name}</p>
                  <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                    <span>{formatFileSize(doc.file_size)}</span>
                    <span>•</span>
                    <span>Téléversé le {formatDate(doc.uploaded_at)}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => handleDownload(doc.id, doc.file_name)}
                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Télécharger"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(doc.id)}
                  disabled={deleteDocumentMutation.isPending}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                  title="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EntityDocuments;

